// Convenios (Agreement) por empresa — reglas de la web.
//
// Contrato del backend (api-doc de Galera, "Agreements"):
//   · Un convenio es GLOBAL (companyId = null, lo ven todas las empresas) o
//     pertenece a UNA empresa.
//   · POST /api/agreement: name, vacationDays, monthlyHours (obligatorios,
//     enteros), holidayType ('laborables' por defecto | 'festivos') y companyId.
//     Superadmin (5): empresa explícita o null (global). Resto: el backend
//     IGNORA companyId y usa la empresa del JWT.
//   · GET: superadmin ve todos; el resto, los de su empresa + los globales.
//   · PUT/DELETE: fuera del superadmin, solo convenios de la propia empresa.
//     Cambiar companyId solo lo puede hacer el superadmin.
//   · personAssignment/assign(-bounded) → 404 "Agreement not found" también
//     cuando el convenio es de OTRA empresa distinta a la del trabajador.
//
// Compatibilidad con el backend ANTIGUO (sin columna companyId): el GET
// devuelve todos los convenios del sistema SIN el campo companyId y el POST/PUT
// ignoran companyId. Por eso distinguimos "companyId: null" (global, backend
// nuevo) de "sin campo companyId" (backend antiguo, dueño desconocido), y el
// filtrado por empresa se sigue haciendo también en cliente como red de
// seguridad.
//
// Los permisos por rol viven aquí y no en src/lib/permissions.js porque ese
// archivo es COMPARTIDO con la app (check:shared) y la app no gestiona
// convenios.

export const GLOBAL_COMPANY_VALUE = 'global';

export const AGREEMENT_NOT_IN_COMPANY_MESSAGE =
  'El convenio no pertenece a la empresa del trabajador (o ya no existe). Elige un convenio de su empresa o uno global.';

const HOLIDAY_TYPE_VALUES = ['laborables', 'festivos'];

const typeOf = (user) => (user?.type == null ? null : Number(user.type));

export const isSuperAdmin = (user) => typeOf(user) === 5;

// Roles que ven la página de Convenios. Supervisor (3) e inspector (4) en
// solo lectura; admin empresa (1) y admin sede (2) gestionan los de su empresa.
export const AGREEMENT_VIEW_ROLES = [1, 2, 3, 4, 5];
const AGREEMENT_COMPANY_MANAGER_ROLES = [1, 2];

export const canViewAgreements = (userType) =>
  userType != null && AGREEMENT_VIEW_ROLES.includes(Number(userType));

// ¿El convenio trae el campo companyId? (backend nuevo). Con el backend
// antiguo la propiedad no existe.
export const hasCompanyField = (agreement) =>
  !!agreement && Object.prototype.hasOwnProperty.call(agreement, 'companyId');

export const isGlobalAgreement = (agreement) =>
  hasCompanyField(agreement) && agreement.companyId === null;

// ¿El listado viene del backend nuevo (convenios con dueño)? Con el antiguo
// ningún convenio trae companyId.
export const listHasCompanyScope = (list) =>
  Array.isArray(list) && list.some(hasCompanyField);

export const canCreateAgreements = (user) => {
  if (isSuperAdmin(user)) return true;
  return AGREEMENT_COMPANY_MANAGER_ROLES.includes(typeOf(user)) && user?.companyId != null;
};

// Editar/borrar: el superadmin cualquiera; admin empresa/sede solo los de SU
// empresa (nunca los globales ni los de dueño desconocido). Supervisor,
// inspector y trabajador, ninguno.
export const canEditAgreement = (user, agreement) => {
  if (!user || !agreement) return false;
  if (isSuperAdmin(user)) return true;
  if (!AGREEMENT_COMPANY_MANAGER_ROLES.includes(typeOf(user))) return false;
  if (user.companyId == null || agreement.companyId == null) return false;
  return Number(agreement.companyId) === Number(user.companyId);
};

export const canDeleteAgreement = canEditAgreement;

export const canChangeAgreementCompany = (user) => isSuperAdmin(user);

// Convenios ofrecibles a la empresa `companyId`: los suyos primero y después
// los globales. Los de dueño desconocido (backend antiguo, sin campo) cuentan
// como globales para no dejar el selector vacío antes del despliegue.
// Sin companyId devuelve el listado tal cual.
export const agreementsForCompany = (list, companyId) => {
  const arr = Array.isArray(list) ? list.filter(Boolean) : [];
  if (companyId == null || companyId === '') return arr;
  const cid = Number(companyId);
  const own = arr.filter((a) => a.companyId != null && Number(a.companyId) === cid);
  const shared = arr.filter((a) => a.companyId == null);
  return [...own, ...shared];
};

// Filas de la página de Convenios según el rol:
//   · superadmin e inspector: todo lo que devuelva el backend.
//   · resto: su empresa + globales. Con el backend ANTIGUO (sin companyId)
//     no hay forma de saber de quién es cada convenio, así que no enseñamos
//     el catálogo de otras empresas: lista vacía (la página avisa).
export const visibleAgreementsForUser = (list, user) => {
  const arr = Array.isArray(list) ? list.filter(Boolean) : [];
  const t = typeOf(user);
  if (t === 5 || t === 4) return arr;
  if (arr.length && !listHasCompanyScope(arr)) return [];
  if (user?.companyId == null) return arr.filter(isGlobalAgreement);
  return agreementsForCompany(arr, user.companyId);
};

// ── Formulario ──────────────────────────────────────────────────────────────

export const companyIdToFormValue = (agreement) => {
  if (isGlobalAgreement(agreement)) return GLOBAL_COMPANY_VALUE;
  if (agreement?.companyId == null) return '';
  return String(agreement.companyId);
};

export const companyIdFromFormValue = (value) => {
  if (value === GLOBAL_COMPANY_VALUE) return null;
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isInteger(n) ? n : undefined;
};

export const emptyAgreementForm = (user) => ({
  name: '',
  vacationDays: 22,
  monthlyHours: 160,
  holidayType: 'laborables',
  companyId: !isSuperAdmin(user) && user?.companyId != null ? String(user.companyId) : '',
});

export const agreementToForm = (agreement) => ({
  name: agreement?.name || '',
  vacationDays: agreement?.vacationDays != null ? Number(agreement.vacationDays) : 22,
  monthlyHours: agreement?.monthlyHours != null ? Number(agreement.monthlyHours) : '',
  holidayType: agreement?.holidayType === 'festivos' ? 'festivos' : 'laborables',
  companyId: companyIdToFormValue(agreement),
});

// Entero estricto: acepta 22 o "22", rechaza "", "22.5" y "abc".
const toInt = (value) => {
  if (value === '' || value == null) return NaN;
  const n = Number(value);
  return Number.isInteger(n) ? n : NaN;
};

const normalizeHolidayType = (value) =>
  HOLIDAY_TYPE_VALUES.includes(value) ? value : 'laborables';

// Devuelve el primer error del formulario (texto para el usuario) o null.
export const validateAgreementForm = (form, user, { isEdit = false } = {}) => {
  if (!String(form?.name || '').trim()) return 'El nombre es obligatorio.';
  const vacationDays = toInt(form.vacationDays);
  if (!Number.isInteger(vacationDays) || vacationDays < 0 || vacationDays > 365) {
    return 'Los días de vacaciones deben ser un número entero entre 0 y 365.';
  }
  const hoursEmpty = form.monthlyHours === '' || form.monthlyHours == null;
  if (!(isEdit && hoursEmpty)) {
    const monthlyHours = toInt(form.monthlyHours);
    if (!Number.isInteger(monthlyHours) || monthlyHours < 1 || monthlyHours > 744) {
      return 'Las horas mensuales deben ser un número entero entre 1 y 744.';
    }
  }
  if (isSuperAdmin(user)) {
    if (!isEdit && form.companyId === '') {
      return 'Elige la empresa propietaria del convenio o márcalo como global.';
    }
  } else if (user?.companyId == null) {
    return 'Tu usuario no tiene empresa asignada: no puedes crear convenios.';
  }
  return null;
};

// Payload del POST. companyId va SIEMPRE: el superadmin elige empresa o
// global (null); el resto manda la suya (el backend nuevo la ignora y usa la
// del JWT; el antiguo ignora el campo entero).
export const buildAgreementCreatePayload = (form, user) => ({
  name: String(form.name || '').trim(),
  vacationDays: toInt(form.vacationDays),
  monthlyHours: toInt(form.monthlyHours),
  holidayType: normalizeHolidayType(form.holidayType),
  companyId: isSuperAdmin(user)
    ? (companyIdFromFormValue(form.companyId) ?? null)
    : Number(user.companyId),
});

// Payload del PUT (actualización parcial). companyId SOLO si lo cambia un
// superadmin: para el resto el backend nuevo lo rechaza y el antiguo lo
// ignora, así que mandarlo sin cambiar solo añade riesgo.
export const buildAgreementUpdatePayload = (form, initial, user) => {
  const payload = {
    name: String(form.name || '').trim(),
    vacationDays: toInt(form.vacationDays),
    holidayType: normalizeHolidayType(form.holidayType),
  };
  const monthlyHours = toInt(form.monthlyHours);
  if (Number.isInteger(monthlyHours)) payload.monthlyHours = monthlyHours;
  if (canChangeAgreementCompany(user)) {
    const next = companyIdFromFormValue(form.companyId);
    const prev = hasCompanyField(initial)
      ? (initial.companyId === null ? null : Number(initial.companyId))
      : undefined;
    if (next !== undefined && next !== prev) payload.companyId = next;
  }
  return payload;
};

// ── Errores de asignación ───────────────────────────────────────────────────

// 404 "Agreement not found" de personAssignment/assign(-bounded): con el
// backend nuevo también significa "convenio de otra empresa".
export const isAgreementNotFoundError = (err) => {
  if (!err || Number(err.status) !== 404) return false;
  const raw = err.data?.message ?? err.bodySnippet ?? '';
  return /agreement not found/i.test(String(raw));
};
